import type { ErrorInfo } from "react";
import { captureReactException } from "@sentry/react";

function checkoutRoute() {
  const path = window.location.pathname;
  return path === "/new-shipment/details" || path === "/new-shipment/pricing"
    ? path
    : undefined;
}

function isNotFoundError(error: unknown): error is object {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "NotFoundError"
  );
}

/** Temporary WEB-A diagnostics. Observe failures; never repair or suppress DOM operations. */
export function installDomRemovalDiagnostics(root: HTMLElement) {
  const nodeIds = new WeakMap<Node, number>();
  let nextId = 0;
  let active = true;
  const observedContainers: string[] = [];
  const restore: (() => void)[] = [];

  function describe(node: Node) {
    let id = nodeIds.get(node);
    if (id === undefined) {
      id = ++nextId;
      nodeIds.set(node, id);
    }
    const role = node instanceof Element ? node.getAttribute("role") : null;
    return {
      localId: id,
      nodeType: node.nodeType,
      kind:
        node === root
          ? "app-root"
          : node === document.body
            ? "body"
            : node.nodeType === Node.DOCUMENT_NODE
              ? "document"
              : "node",
      tag: node instanceof Element ? node.localName : undefined,
      overlayRole:
        role &&
        ["dialog", "alertdialog", "listbox", "menu", "tooltip"].includes(role)
          ? role
          : undefined,
      connected: node.isConnected,
      withinAppRoot: root.contains(node),
    };
  }

  function baseContext(route: string) {
    return {
      diagnosticVersion: 1,
      route,
      observedContainers: [...observedContainers],
      translatedLtr:
        document.documentElement.classList.contains("translated-ltr"),
      translatedRtl:
        document.documentElement.classList.contains("translated-rtl"),
    };
  }

  function failureContext(route: string, container: Node, child: Node) {
    const actualAncestors = [];
    let parent = child.parentNode;
    while (parent && actualAncestors.length < 4) {
      actualAncestors.push(describe(parent));
      parent = parent.parentNode;
    }
    return {
      ...baseContext(route),
      capture: "native-failure",
      operation: "removeChild",
      requestedParent: describe(container),
      child: describe(child),
      // Keep descriptors at the same depth as child/requestedParent: Sentry
      // normalizes deeper objects inside arrays to "[Object]" by default.
      actualAncestor0: actualAncestors[0],
      actualAncestor1: actualAncestors[1],
      actualAncestor2: actualAncestors[2],
      actualAncestor3: actualAncestors[3],
      childParentMatchesContainer: child.parentNode === container,
      sameDocument: child.ownerDocument === container.ownerDocument,
    };
  }
  const failures = new WeakMap<object, ReturnType<typeof failureContext>>();

  // Only these two container instances are decorated, never Node.prototype.
  // React Aria's overlays use body; custom portal containers remain unknown.
  for (const container of [root, document.body]) {
    const previousDescriptor = Object.getOwnPropertyDescriptor(
      container,
      "removeChild"
    );
    const original = container.removeChild;
    function removeChild<T extends Node>(this: Node, child: T): T {
      try {
        // Function.call erases the native method's generic return subtype.
        return original.call(this, child) as T;
      } catch (error) {
        try {
          const route = checkoutRoute();
          if (active && this === container && route && isNotFoundError(error)) {
            failures.set(error, failureContext(route, container, child));
          }
        } catch {
          // Diagnostic failure must not replace the native exception.
        }
        throw error;
      }
    }
    try {
      Object.defineProperty(container, "removeChild", {
        configurable: true,
        writable: true,
        enumerable: previousDescriptor?.enumerable ?? false,
        value: removeChild,
      });
      observedContainers.push(container === root ? "app-root" : "body");
      restore.push(() => {
        if (container.removeChild !== removeChild) return;
        if (previousDescriptor)
          Object.defineProperty(container, "removeChild", previousDescriptor);
        else Reflect.deleteProperty(container, "removeChild");
      });
    } catch {
      // React component stacks remain available if a container cannot be observed.
    }
  }

  return {
    onUncaughtError(error: unknown, info: ErrorInfo) {
      let context:
        | (ReturnType<typeof baseContext> & {
            capture: string;
            operation: string;
          })
        | undefined;
      try {
        if (active && isNotFoundError(error)) {
          const route = checkoutRoute();
          context =
            failures.get(error) ??
            (route
              ? {
                  ...baseContext(route),
                  capture: "react-only",
                  operation: "unknown",
                }
              : undefined);
          failures.delete(error);
        }
      } catch {
        // Still report the original React exception if metadata is unavailable.
      }
      captureReactException(error, info, {
        mechanism: {
          handled: false,
          type: "auto.function.react.error_handler",
        },
        ...(context
          ? { captureContext: { contexts: { dom_removal: context } } }
          : {}),
      });
    },
    dispose() {
      active = false;
      for (const undo of restore) undo();
    },
  };
}
